import {useState} from 'react';
import PageShell from '../components/PageShell';
import {
  AGE_BANDS,
  RISK_STEPS,
  RESULTS,
  ADA_CREDIT,
  ASSESSMENT_DISCLAIMER,
  scoreRisk,
} from '../utils/AssessmentsData';
import './Assessments.css';

const TONE = {
  low: 'positive',
  moderate: 'pending',
  high: 'attention',
};

// Simple implementations of the tokens and components used in the new UI
const TOKENS = {
  ink: '#1A1A2E',
  inkMuted: '#6B6B7A',
  inkSoft: '#8F8FA3',
  primary: '#C43B71',
  positive: '#1F7A54',
};

export default function Assessments() {
  const [band, setBand] = useState(null);
  const [started, setStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const steps = band ? RISK_STEPS[band] : [];
  const step = steps[stepIndex];
  const stepAnswered = step ? step.questions.every((q) => answers[q.id]) : false;

  const restart = () => {
    setBand(null);
    setStarted(false);
    setStepIndex(0);
    setAnswers({});
    setDone(false);
  };

  return (
    <PageShell title="Assessments" showBackButton={true}>
      <div className="assessments-container">
        <h2 className="assessments-title">Caries risk check</h2>

        {/* ── Intro and age band ── */}
        {!started && (
          <>
            <p className="assessments-intro-text">
              Caries is the clinical word for tooth decay. This check asks the questions a dentist works
              through to judge how likely decay is for you, and tells you what raised your risk. It takes
              about two minutes.
            </p>

            <div className="cr-card" style={{padding: '10px 14px'}}>
              <fieldset className="choice-group">
                <legend className="choice-group-legend">Who is this for?</legend>
                <div className="choice-group-items">
                  {AGE_BANDS.map((option) => (
                    <ChoiceRow
                      key={option.id}
                      id={`band-${option.id}`}
                      name="band"
                      label={option.label}
                      description={option.description}
                      checked={band === option.id}
                      onChange={() => setBand(option.id)}
                    />
                  ))}
                </div>
              </fieldset>
            </div>

            <button className="cr-btn-primary" disabled={!band} onClick={() => setStarted(true)}>
              Start the check
            </button>

            <div className="cr-note">{ASSESSMENT_DISCLAIMER}</div>
          </>
        )}

        {/* ── Questions ── */}
        {started && !done && step && (
          <>
            <Progress current={stepIndex + 1} total={steps.length} title={step.title} />

            {step.intro && <p className="cr-step-intro">{step.intro}</p>}

            {step.questions.map((question) => (
              <div key={question.id} className="cr-card" style={{padding: '14px 14px 10px'}}>
                <fieldset className="choice-group">
                  <legend className="choice-group-legend">{question.prompt}</legend>
                  {question.hint && <p className="choice-group-hint">{question.hint}</p>}
                  <div className="choice-group-items">
                    {question.options.map((option) => (
                      <ChoiceRow
                        key={option.id}
                        id={`${question.id}-${option.id}`}
                        name={question.id}
                        label={option.label}
                        checked={answers[question.id] === option.id}
                        onChange={() => setAnswers((a) => ({...a, [question.id]: option.id}))}
                      />
                    ))}
                  </div>
                </fieldset>
              </div>
            ))}

            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              <button
                className="cr-btn-primary"
                disabled={!stepAnswered}
                onClick={() => {
                  if (stepIndex + 1 < steps.length) {
                    setStepIndex((i) => i + 1);
                    window.scrollTo({top: 0});
                  } else {
                    setDone(true);
                    window.scrollTo({top: 0});
                  }
                }}
              >
                {stepIndex + 1 < steps.length ? 'Continue' : 'See my result'}
              </button>
              <button
                className="cr-btn-secondary"
                onClick={() => {
                  if (stepIndex === 0) restart();
                  else setStepIndex((i) => i - 1);
                  window.scrollTo({top: 0});
                }}
              >
                Back
              </button>
            </div>
          </>
        )}

        {/* ── Result ── */}
        {done && band && <Result band={band} answers={answers} onRestart={restart} />}
      </div>
    </PageShell>
  );
}

function ChoiceRow({id, name, label, description, checked, onChange}) {
  return (
    <label
      htmlFor={id}
      className="choice-row"
      style={{
        background: checked ? 'rgba(196,59,113,0.07)' : 'transparent',
      }}
    >
      <input
        id={id}
        name={name}
        type="radio"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="ft-sr-only-input"
      />
      <span
        aria-hidden="true"
        className="choice-row-circle"
        style={{
          background: checked ? 'linear-gradient(135deg, #FF6B9E, #C43B71)' : 'rgba(196,59,113,0.08)',
          boxShadow: checked ? 'none' : 'inset 0 0 0 1.5px rgba(196,59,113,0.25)',
        }}
      >
        {checked && <span className="choice-row-dot" />}
      </span>
      <span className="choice-row-text">
        <span className="choice-row-label">{label}</span>
        {description && <span className="choice-row-desc">{description}</span>}
      </span>
    </label>
  );
}

function Result({band, answers, onRestart}) {
  const {level, drivers, unknowns} = scoreRisk(band, answers);
  const copy = RESULTS[level];

  return (
    <>
      <div className="cr-card" style={{padding: '18px 18px 20px'}}>
        <StatusPill tone={TONE[level]}>
          {level === 'low' ? 'Low' : level === 'moderate' ? 'Moderate' : 'High'}
        </StatusPill>
        <h3 className="result-title">{copy.title}</h3>
        <p className="result-body">{copy.body}</p>
      </div>

      {drivers.length > 0 && (
        <ResultList title="What raised your risk" items={drivers} tone={TOKENS.primary} />
      )}

      <ResultList title="What usually helps" items={copy.next} tone={TOKENS.positive} />

      {unknowns.length > 0 && (
        <ResultList
          title="Worth asking your dentist"
          items={unknowns}
          tone={TOKENS.inkSoft}
          body="You answered “not sure” to these. They are things a dentist can see in seconds."
        />
      )}

      <button className="cr-btn-secondary" onClick={onRestart}>
        Start over
      </button>

      <div className="cr-note">{ASSESSMENT_DISCLAIMER}</div>
      <div className="cr-note">{ADA_CREDIT}</div>
    </>
  );
}

function ResultList({title, items, tone, body}) {
  return (
    <section>
      <h3 className="result-list-title">{title}</h3>
      {body && <p className="result-list-body">{body}</p>}
      <div className="cr-card" style={{padding: '12px 16px'}}>
        <ul className="result-list-ul">
          {items.map((item) => (
            <li key={item} className="result-list-li">
              <span aria-hidden="true" className="result-list-dot" style={{background: tone}} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Progress({current, total, title}) {
  return (
    <div>
      <div className="progress-header">
        <h3 className="progress-title">{title}</h3>
        <span className="progress-count">
          Step {current} of {total}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={current}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-label={`Step ${current} of ${total}`}
        className="progress-track"
      >
        <div className="progress-fill" style={{width: `${(current / total) * 100}%`}} />
      </div>
    </div>
  );
}

function StatusPill({tone = 'neutral', children}) {
  const TONES = {
    neutral: {fg: TOKENS.inkMuted, bg: 'rgba(26, 26, 46, 0.06)'},
    pending: {fg: TOKENS.primary, bg: 'rgba(196, 59, 113, 0.06)'},
    positive: {fg: TOKENS.positive, bg: 'rgba(31, 122, 84, 0.10)'},
    attention: {fg: '#fff', bg: TOKENS.primary},
  };
  const t = TONES[tone] || TONES.neutral;

  return (
    <span className="status-pill" style={{color: t.fg, background: t.bg}}>
      {children}
    </span>
  );
}
